import { useEffect, useRef } from 'react';

/**
 * Reveals descendants marked `.ed-scroll-reveal` as they scroll into view, the
 * effect the static mock drove with a scroll listener. Pass `enabled: false`
 * while the content is still loading so the observer attaches to real nodes.
 */
export function useScrollReveal<T extends HTMLElement>(enabled: boolean) {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('ed-revealed');
          // One-way: once revealed an element stays revealed.
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -80px 0px' },
    );

    for (const el of container.querySelectorAll('.ed-scroll-reveal')) observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  return containerRef;
}
